import Box from '@mui/material/Box';
import { AUTHZ_FEATURES } from '../../../../constants/authz';
import { buildPermissionTreeSections } from '../../utils/permissionTreeUtils';
import { BUSINESS_FUNCTIONS_MODULE_CODENAME } from '../../utils/businessFunctionsPermissionUtils';
import PermissionModuleSection from './PermissionModuleSection';

const EDTECH_FEATURE_GROUPS = [
  {
    codename: 'edtech_dashboards',
    label: 'EdTech Dashboards',
    features: [
      ['CONCEPT_BUILDER', 'Concept Builder'],
      ['PRACTICE_TEST', 'Practice Test'],
      ['KNOWLEDGE_MAP', 'Knowledge Map'],
      ['PERFORMANCE_MAP', 'Performance Map'],
      ['PARENT_DASHBOARD', 'Parent Dashboard'],
      ['TEACHER_DASHBOARD', 'Teacher Dashboard'],
      ['ACADEMIC_INFO', 'Academic Info'],
      ['QUESTION_BANK', 'Question Bank'],
      ['SYLLABUS_BUILDER', 'Syllabus Builder'],
    ],
  },
  {
    codename: 'edtech_ai_tools',
    label: 'AI & Tools',
    features: [
      ['AI', 'AI'],
      ['VECTOR_DATABASE', 'Vector Database'],
      ['TOOLS', 'Tools'],
    ],
  },
];

const EDTECH_FEATURE_CODENAMES = new Set(
  EDTECH_FEATURE_GROUPS.flatMap((group) =>
    group.features.map(([featureKey]) => AUTHZ_FEATURES.EDTECH[featureKey])
  ).concat(AUTHZ_FEATURES.RECORDS.MOVE)
);

const EDTECH_PERMISSION_MODULE = {
  codename: 'edtech_ai_tools',
  label: 'EdTech, AI & Tools',
  description: 'Control access to EdTech dashboards, AI, the vector database, and tools.',
  children: EDTECH_FEATURE_GROUPS.map((group) => ({
    codename: group.codename,
    label: group.label,
    features: group.features.map(([featureKey, name]) => ({
      codename: AUTHZ_FEATURES.EDTECH[featureKey],
      name,
    })),
  })),
};

const REPORTING_ANALYTICS_FEATURE_CODENAMES = new Set([
  AUTHZ_FEATURES.REPORTING.VIEW,
  AUTHZ_FEATURES.ANALYTICS.VIEW,
  AUTHZ_FEATURES.ANALYTICS.CREATE,
  AUTHZ_FEATURES.ANALYTICS.DELETE,
  AUTHZ_FEATURES.ANALYTICS.EXPORT,
  AUTHZ_FEATURES.ANALYTICS.RECORDS,
  AUTHZ_FEATURES.ANALYTICS.WHATSAPP,
  AUTHZ_FEATURES.ANALYTICS.INTERVENE,
]);

const REPORTING_ANALYTICS_PERMISSION_MODULE = {
  codename: 'reporting_analytics',
  label: 'Reporting & Analytics',
  description: 'Legacy entity reporting and platform analytics (custom reports).',
  children: [
    {
      codename: 'entity_reporting',
      label: 'Reporting',
      features: [
        {
          codename: AUTHZ_FEATURES.REPORTING.VIEW,
          name: 'Reporting (sidebar)',
        },
      ],
    },
    {
      codename: 'platform_analytics',
      label: 'Analytics',
      features: [
        { codename: AUTHZ_FEATURES.ANALYTICS.VIEW, name: 'View Analytics' },
        { codename: AUTHZ_FEATURES.ANALYTICS.CREATE, name: 'Create custom reports' },
        { codename: AUTHZ_FEATURES.ANALYTICS.DELETE, name: 'Delete custom reports' },
        { codename: AUTHZ_FEATURES.ANALYTICS.EXPORT, name: 'Export analytics' },
        { codename: AUTHZ_FEATURES.ANALYTICS.RECORDS, name: 'Analytics — Records dataset' },
        { codename: AUTHZ_FEATURES.ANALYTICS.WHATSAPP, name: 'Analytics — WhatsApp dataset' },
        { codename: AUTHZ_FEATURES.ANALYTICS.INTERVENE, name: 'Analytics — Intervene dataset' },
      ],
    },
  ],
};

function omitReportingAnalyticsFeatures(node) {
  return {
    ...node,
    features: (node.features || []).filter(
      (feature) => !REPORTING_ANALYTICS_FEATURE_CODENAMES.has(feature.codename)
    ),
    children: (node.children || []).map(omitReportingAnalyticsFeatures),
  };
}

function omitEdTechFeatures(node) {
  return {
    ...node,
    features: (node.features || []).filter(
      (feature) => !EDTECH_FEATURE_CODENAMES.has(feature.codename)
    ),
    children: (node.children || []).map(omitEdTechFeatures),
  };
}

export default function ProfilePermissionsEditor({
  catalog,
  grants,
  disabled = false,
  onNodeGrantChange,
  onFeatureChange,
  onObjectChange,
}) {
  const sections = buildPermissionTreeSections({
    ...catalog,
    modules: [
      ...(catalog?.modules || [])
        .map((module) => omitReportingAnalyticsFeatures(omitEdTechFeatures(module)))
        .filter((module) => module.codename !== 'reporting_management'),
      REPORTING_ANALYTICS_PERMISSION_MODULE,
      EDTECH_PERMISSION_MODULE,
    ],
  }).filter(
    (section) => section.codename !== BUSINESS_FUNCTIONS_MODULE_CODENAME
  );

  if (!sections.length) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {sections.map((section, index) => (
        <PermissionModuleSection
          key={section.codename}
          section={section}
          grants={grants}
          disabled={disabled}
          defaultExpanded={index === 0}
          onNodeGrantChange={onNodeGrantChange}
          onFeatureChange={onFeatureChange}
          onObjectChange={onObjectChange}
        />
      ))}
    </Box>
  );
}
